import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import Select from "react-select";
import "./TransazioniRavvicinate.css";
import RotateDevice from "../components/RotateDevice/RotateDevice";
import DatePickerModal from "../components/DatePickerModal/DatePickerModal";
import Lf23Loader from "../components/Lf23Loader";

const TransazioniRavvicinate = () => {
    const navigate = useNavigate();
    const API_URL = process.env.REACT_APP_API_URL || "http://localhost:3001/api";

    const [dati, setDati] = useState([]);
    const [page, setPage] = useState(1);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false);
    const [limit, setLimit] = useState(20);
    const [showInfo, setShowInfo] = useState(false);

    // UI State
    const [viewMode, setViewMode] = useState("table");
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [noteFeedback, setNoteFeedback] = useState(null);

    useEffect(() => {
        const handleEsc = (e) => {
            if (e.key === 'Escape' && isFullscreen) setIsFullscreen(false);
        };
        document.addEventListener('keydown', handleEsc);
        return () => document.removeEventListener('keydown', handleEsc);
    }, [isFullscreen]);
    const [showFilters, setShowFilters] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const [expandedRows, setExpandedRows] = useState(new Set());

    const [sortConfig, setSortConfig] = useState({ key: 'data_prima', direction: 'desc' });

    const handleSort = (key) => {
        setSortConfig(prev =>
            prev.key === key
                ? { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' }
                : { key, direction: 'asc' }
        );
    };

    // Stats globali
    const [stats, setStats] = useState({
        totalImporto: 0,
        avgImporto: 0
    });

    const toggleRowExpansion = (idx) => {
        const newExpanded = new Set(expandedRows);
        if (newExpanded.has(idx)) {
            newExpanded.delete(idx);
        } else {
            newExpanded.add(idx);
        }
        setExpandedRows(newExpanded);
    };

    // Helper function to get default date range (last 7 days)
    const getDefaultDateRange = () => {
        const today = new Date();
        const sevenDaysAgo = new Date(today);
        sevenDaysAgo.setDate(today.getDate() - 7);

        const formatDate = (date) => {
            return date.toISOString().split('T')[0]; // YYYY-MM-DD format
        };

        return {
            from: formatDate(sevenDaysAgo),
            to: formatDate(today)
        };
    };

    const defaultDates = getDefaultDateRange();

    // Filtri
    const [fromDate, setFromDate] = useState(defaultDates.from);
    const [toDate, setToDate] = useState(defaultDates.to);
    const [targa, setTarga] = useState("");
    const [driver, setDriver] = useState("");
    const [alimentazioni, setAlimentazioni] = useState([{ value: "Tutti", label: "Tutti" }]);
    const [costo, setCosto] = useState([
        { value: "0-20", label: "0-20€" },
        { value: "21-50", label: "21-50€" },
        { value: "50+", label: ">50€" }
    ]);

    const alimentazioneOptions = [
        { value: "Tutti", label: "Tutti" },
        { value: "GPL", label: "GPL" },
        { value: "Diesel", label: "Diesel" },
        { value: "Metano", label: "Metano" },
        { value: "Elettrico", label: "Elettrico" },
        { value: "Benzina", label: "Benzina" }
    ];

    const costoOptions = [
        { value: "0-20", label: "0-20€" },
        { value: "21-50", label: "21-50€" },
        { value: "50+", label: ">50€" }
    ];

    const handleAlimentazioneChange = (selected) => {
        if (!selected || selected.length === 0) {
            setAlimentazioni([{ value: "Tutti", label: "Tutti" }]);
        } else if (selected.find(opt => opt.value === "Tutti") && selected.length > 1) {
            setAlimentazioni(selected.filter(opt => opt.value !== "Tutti"));
        } else if (selected.find(opt => opt.value === "Tutti") && selected.length === 1) {
            setAlimentazioni([{ value: "Tutti", label: "Tutti" }]);
        } else if (selected.length === alimentazioneOptions.length - 1 && !selected.find(opt => opt.value === "Tutti")) {
            setAlimentazioni(alimentazioneOptions);
        } else {
            setAlimentazioni(selected);
        }
    };

    const fetchStats = async () => {
        const selectedAlim = alimentazioni.map(a => a.value).join(",");
        const selectedCosto = costo.map(c => c.value).join(",");

        try {
            const res = await axios.get(`${API_URL}/dkv/transazioni-ravvicinate/stats`, {
                params: {
                    fromDate: fromDate || undefined,
                    toDate: toDate || undefined,
                    targa,
                    driver,
                    alimentazioni: selectedAlim,
                    costo: selectedCosto
                }
            });

            setStats({
                totalImporto: res.data.totalImporto,
                avgImporto: res.data.avgImporto
            });
        } catch (err) {
            console.error("Errore nel caricamento statistiche:", err);
        }
    };

    const fetchData = async () => {
        const selectedAlim = alimentazioni.map(a => a.value).join(",");
        const selectedCosto = costo.map(c => c.value).join(",");

        setLoading(true); // Inizio caricamento

        try {
            const res = await axios.get(`${API_URL}/dkv/transazioni-ravvicinate`, {
                params: {
                    page,
                    limit,
                    fromDate: fromDate || undefined,
                    toDate: toDate || undefined,
                    targa,
                    driver,
                    alimentazioni: selectedAlim,
                    costo: selectedCosto,
                    sortBy: sortConfig.key || undefined,
                    sortDir: sortConfig.direction || undefined
                }
            });
            setDati(res.data.data);
            setTotal(res.data.total);
        } catch (err) {
            console.error("Errore nella fetch:", err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
        fetchStats(); // Fetch anche le statistiche
        // eslint-disable-next-line
    }, [page, limit, sortConfig]);

    useEffect(() => {
        setPage(1);
    }, [sortConfig]);

    const totalPages = Math.ceil(total / limit);

    const handleReset = () => {
        const defaultDates = getDefaultDateRange();
        setFromDate(defaultDates.from);
        setToDate(defaultDates.to);
        setTarga("");
        setDriver("");
        setAlimentazioni([{ value: "Tutti", label: "Tutti" }]);
        setCosto([
            { value: "0-20", label: "0-20€" },
            { value: "21-50", label: "21-50€" },
            { value: "50+", label: ">50€" }
        ]);
        setSearchTerm("");
        setPage(1);
        fetchData();
        fetchStats();
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (fromDate && toDate && new Date(fromDate) > new Date(toDate)) {
            alert("La data iniziale non può essere successiva alla data finale.");
            return;
        }
        setPage(1);
        fetchData();
        fetchStats();
    };

    const filteredDati = dati.filter(row => {
        if (!searchTerm) return true;
        const search = searchTerm.toLowerCase();
        return (
            row.targa_dell_automezzo?.toLowerCase().includes(search) ||
            row.driver_primo?.toLowerCase().includes(search) ||
            row.driver_secondo?.toLowerCase().includes(search) ||
            row.alimentazione?.toLowerCase().includes(search)
        );
    });

    return (
        <div className="transazioni-container">
            <header className="page-header">
                <div className="header-content">
                    <button className="back-button" onClick={() => navigate('/produttivita')}>
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M19 12H5M12 19l-7-7 7-7" />
                        </svg>
                        Indietro
                    </button>
                    <div className="header-title">
                        <div className="header-title-row">
                            <h2>Transazioni Ravvicinate</h2>
                            <button
                                className="info-button-modern"
                                onClick={() => setShowInfo(!showInfo)}
                                title="Informazioni"
                            >
                                <span className="info-icon-text">i</span>
                            </button>
                        </div>
                        <p className="header-subtitle">
                            Monitoraggio rifornimenti effettuati a distanza temporale ravvicinata (&lt; 2 ore)
                        </p>
                    </div>
                </div>
            </header>

            {/* Modal Informazioni */}
            {showInfo && (
                <div className="modal-overlay" onClick={() => setShowInfo(false)}>
                    <div className="modal-container" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <div className="modal-icon">
                                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"></path>
                                    <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"></path>
                                    <path d="M10 8h6"></path>
                                    <path d="M10 12h6"></path>
                                    <path d="M10 16h4"></path>
                                </svg>
                            </div>
                            <h3>Informazioni sulla pagina</h3>
                            <button className="modal-close" onClick={() => setShowInfo(false)}>
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <line x1="18" y1="6" x2="6" y2="18"></line>
                                    <line x1="6" y1="6" x2="18" y2="18"></line>
                                </svg>
                            </button>
                        </div>
                        <div className="modal-body">
                            <div className="modal-section">
                                <h4>Cosa fa questa pagina?</h4>
                                <p>
                                    Questa sezione evidenzia le <strong>anomalie nei rifornimenti</strong> dovute a transazioni effettuate a distanza temporale
                                    ravvicinata (meno di 2 ore). I dati sono estratti dalla piattaforma DKV e analizzati per individuare potenziali situazioni
                                    sospette come rifornimenti multipli, incoerenze tra carburanti e utilizzo delle stesse carte.
                                </p>
                            </div>
                            <div className="modal-section">
                                <h4>Come utilizzare i filtri</h4>
                                <p>
                                    Utilizza i filtri personalizzati per raffinare la ricerca in base a:
                                </p>
                                <ul>
                                    <li><strong>Periodo</strong> - Seleziona l'intervallo temporale di analisi</li>
                                    <li><strong>Targa</strong> - Filtra per veicolo specifico</li>
                                    <li><strong>Conducente</strong> - Cerca per driver</li>
                                    <li><strong>Tipo di alimentazione</strong> - Filtra per carburante</li>
                                    <li><strong>Fascia di costo</strong> - Raggruppa per range di importo totale</li>
                                </ul>
                                <p className="modal-tip">
                                    💡 La colonna <strong>Delta</strong> mostra il tempo trascorso tra le due transazioni. Usa i pulsanti <strong>Filtra</strong> e <strong>Reset filtro</strong> per gestire rapidamente i criteri.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <div className="content-area">
                {/* Top Controls */}
                <div className="top-controls">
                    <div className="controls-left">
                        <button
                            className={`toggle-filters-btn ${showFilters ? 'active' : ''}`}
                            onClick={() => setShowFilters(!showFilters)}
                        >
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"></polygon>
                            </svg>
                            {showFilters ? 'Nascondi Filtri' : 'Mostra Filtri'}
                        </button>

                        <div className="search-bar-compact">
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <circle cx="11" cy="11" r="8"></circle>
                                <path d="m21 21-4.35-4.35"></path>
                            </svg>
                            <input
                                type="text"
                                placeholder="Cerca..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </div>
                    </div>

                    <div className="controls-right">
                        <div className="view-toggle-compact">
                            <button
                                className={`view-btn-compact ${viewMode === 'table' ? 'active' : ''}`}
                                onClick={() => setViewMode('table')}
                            >
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M3 9h18M3 15h18M9 3v18"></path>
                                </svg>
                                Tabella
                            </button>
                            <button
                                className={`view-btn-compact ${viewMode === 'cards' ? 'active' : ''}`}
                                onClick={() => setViewMode('cards')}
                            >
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <rect x="3" y="3" width="7" height="7"></rect>
                                    <rect x="14" y="3" width="7" height="7"></rect>
                                    <rect x="14" y="14" width="7" height="7"></rect>
                                    <rect x="3" y="14" width="7" height="7"></rect>
                                </svg>
                                Cards
                            </button>
                        </div>
                    </div>
                </div>

                {/* Filters Panel */}
                {showFilters && (
                    <div className="filters-panel">
                        <form className="filters-form" onSubmit={handleSubmit}>
                            <div className="filters-grid">
                                <div className="filter-item">
                                    <label>Dal</label>
                                    <DatePickerModal
                                        value={fromDate}
                                        onChange={(e) => setFromDate(e.target.value)}
                                        max={toDate || undefined}
                                        label="Dal"
                                    />
                                </div>

                                <div className="filter-item">
                                    <label>Al</label>
                                    <DatePickerModal
                                        value={toDate}
                                        onChange={(e) => setToDate(e.target.value)}
                                        min={fromDate || undefined}
                                        label="Al"
                                    />
                                </div>

                                <div className="filter-item">
                                    <label>Targa</label>
                                    <input
                                        type="text"
                                        placeholder="Targa..."
                                        value={targa}
                                        onChange={(e) => setTarga(e.target.value.trim())}
                                    />
                                </div>

                                <div className="filter-item">
                                    <label>Driver</label>
                                    <input
                                        type="text"
                                        placeholder="Driver..."
                                        value={driver}
                                        onChange={(e) => setDriver(e.target.value.trim())}
                                    />
                                </div>

                                <div className="filter-item">
                                    <label>Alimentazione</label>
                                    <Select
                                        isMulti
                                        classNamePrefix="rs"
                                        options={alimentazioneOptions}
                                        value={alimentazioni}
                                        onChange={handleAlimentazioneChange}
                                    />
                                </div>

                                <div className="filter-item">
                                    <label>Costo</label>
                                    <Select
                                        isMulti
                                        classNamePrefix="rs"
                                        options={costoOptions}
                                        value={costo}
                                        onChange={setCosto}
                                    />
                                </div>

                                <div className="filter-item filter-actions">
                                    <label>&nbsp;</label>
                                    <div className="filter-buttons">
                                        <button type="submit" className="btn-apply-compact">Filtra</button>
                                        <button type="button" className="btn-reset-compact" onClick={handleReset}>Reset filtro</button>
                                    </div>
                                </div>
                            </div>
                        </form>
                    </div>
                )}

                {/* Stats Cards */}
                <div className="stats-grid-compact">
                    <div className="stat-card-compact stat-primary">
                        <div className="stat-icon-compact">
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                                <circle cx="12" cy="7" r="4"></circle>
                            </svg>
                        </div>
                        <div className="stat-content-compact">
                            <p className="stat-label-compact">Transazioni</p>
                            <p className="stat-value-compact">{total}</p>
                        </div>
                    </div>

                    <div className="stat-card-compact stat-success">
                        <div className="stat-icon-compact">
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <line x1="12" y1="1" x2="12" y2="23"></line>
                                <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path>
                            </svg>
                        </div>
                        <div className="stat-content-compact">
                            <p className="stat-label-compact">Importo Totale</p>
                            <p className="stat-value-compact">{stats.totalImporto.toFixed(2)} €</p>
                        </div>
                    </div>

                    <div className="stat-card-compact stat-warning">
                        <div className="stat-icon-compact">
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <line x1="18" y1="20" x2="18" y2="10"></line>
                                <line x1="12" y1="20" x2="12" y2="4"></line>
                                <line x1="6" y1="20" x2="6" y2="14"></line>
                            </svg>
                        </div>
                        <div className="stat-content-compact">
                            <p className="stat-label-compact">Media Importo</p>
                            <p className="stat-value-compact">{stats.avgImporto.toFixed(2)} €</p>
                        </div>
                    </div>

                    <div className="stat-card-compact stat-info">
                        <div className="stat-icon-compact">
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                                <line x1="16" y1="2" x2="16" y2="6"></line>
                                <line x1="8" y1="2" x2="8" y2="6"></line>
                                <line x1="3" y1="10" x2="21" y2="10"></line>
                            </svg>
                        </div>
                        <div className="stat-content-compact">
                            <p className="stat-label-compact">Periodo</p>
                            <p className="stat-value-compact">
                                {fromDate && toDate
                                    ? `${new Date(fromDate).toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit' })} - ${new Date(toDate).toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit' })}`
                                    : 'Non specificato'}
                            </p>
                        </div>
                    </div>
                </div>
                {loading ? (
                    <Lf23Loader />
                ) : (
                    <>
                        {viewMode === 'table' && (
                            <>
                            <div className="fullscreen-btn-row">
                                <button
                                    className="fullscreen-btn-compact"
                                    onClick={() => setIsFullscreen(!isFullscreen)}
                                    title={isFullscreen ? 'Esci da tutto schermo' : 'Tutto schermo'}
                                >
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        {isFullscreen ? (
                                            <>
                                                <polyline points="4 14 10 14 10 20"></polyline>
                                                <polyline points="20 10 14 10 14 4"></polyline>
                                                <line x1="14" y1="10" x2="21" y2="3"></line>
                                                <line x1="3" y1="21" x2="10" y2="14"></line>
                                            </>
                                        ) : (
                                            <>
                                                <polyline points="15 3 21 3 21 9"></polyline>
                                                <polyline points="9 21 3 21 3 15"></polyline>
                                                <line x1="21" y1="3" x2="14" y2="10"></line>
                                                <line x1="3" y1="21" x2="10" y2="14"></line>
                                            </>
                                        )}
                                    </svg>
                                </button>
                            </div>
                            <div className={`table-container-compact${isFullscreen ? ' fullscreen-table' : ''}`}>
                                <RotateDevice active={isFullscreen} />
                                {isFullscreen && (
                                    <div className="fullscreen-exit-bar">
                                        <button
                                            className="fullscreen-btn-compact"
                                            onClick={() => setIsFullscreen(false)}
                                            title="Esci da tutto schermo"
                                        >
                                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                <polyline points="4 14 10 14 10 20"></polyline>
                                                <polyline points="20 10 14 10 14 4"></polyline>
                                                <line x1="14" y1="10" x2="21" y2="3"></line>
                                                <line x1="3" y1="21" x2="10" y2="14"></line>
                                            </svg>
                                        </button>
                                    </div>
                                )}
                                <div className="table-scroll-area">
                                <table className="modern-table-compact">
                                    <thead>
                                        <tr>
                                            <th style={{ width: '50px', textAlign: 'center' }}></th>
                                            {[
                                                { label: 'Data', key: 'data_prima' },
                                                { label: 'Targa', key: 'targa_dell_automezzo' },
                                                { label: 'Alimentazione', key: 'alimentazione' },
                                                { label: 'Importo Totale', key: 'importo_totale' },
                                                { label: 'Delta Tempo', key: 'min_trascorsi' },
                                                { label: 'Gestito', key: 'gestito' },
                                            ].map(({ label, key }) => (
                                                <th key={key} className="transazioni-th-sortable" onClick={() => handleSort(key)} style={label === 'Gestito' ? { textAlign: 'center' } : {}}>
                                                    {label}
                                                    <span className="transazioni-sort-icon">
                                                        {sortConfig.key === key ? (sortConfig.direction === 'asc' ? ' ▲' : ' ▼') : ' ⇅'}
                                                    </span>
                                                </th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filteredDati.map((row, idx) => (
                                            <React.Fragment key={idx}>
                                                <tr className="table-row-compact" onClick={() => toggleRowExpansion(idx)}>
                                                    <td className="expand-cell-compact">
                                                        <button className="expand-btn-compact">
                                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{
                                                                transform: expandedRows.has(idx) ? 'rotate(180deg)' : 'rotate(0deg)',
                                                                transition: 'transform 0.2s'
                                                            }}>
                                                                <polyline points="6 9 12 15 18 9"></polyline>
                                                            </svg>
                                                        </button>
                                                    </td>
                                                    <td style={{ fontWeight: '500' }}>{new Date(row.data_prima).toLocaleString("it-IT")}</td>
                                                    <td>
                                                        <span className="badge-compact badge-targa">{row.targa_dell_automezzo}</span>
                                                    </td>
                                                    <td>
                                                        <span className="badge-compact badge-fuel">{row.alimentazione}</span>
                                                    </td>
                                                    <td style={{ fontWeight: '700', color: '#0F172A' }}>
                                                        {(row.valore_primo + row.valore_secondo).toFixed(2)} €
                                                    </td>
                                                    <td>
                                                        <span className="badge-compact badge-alert">
                                                            {row.min_trascorsi
                                                                ? `${row.min_trascorsi.hours || 0}h ${row.min_trascorsi.minutes || 0}m`
                                                                : "-"}
                                                        </span>
                                                    </td>
                                                    <td className="expand-cell-compact" onClick={(e) => e.stopPropagation()}>
                                                        <input
                                                            type="checkbox"
                                                            checked={row.gestito}
                                                            style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                                                            onChange={async () => {
                                                                const nuovoStato = !row.gestito;
                                                                try {
                                                                    await axios.post(`${API_URL}/dkv/gestito-ravvicinate`, {
                                                                        targa: row["targa_dell_automezzo"],
                                                                        data_1: row["data_prima"],
                                                                        data_2: row["data_seconda"],
                                                                        gestito: nuovoStato,
                                                                        note: row.note
                                                                    });

                                                                    setDati(prev => {
                                                                        const nuovo = [...prev];
                                                                        const originalIdx = dati.findIndex(d => d.targa_dell_automezzo === row.targa_dell_automezzo && d.data_prima === row.data_prima);
                                                                        if (originalIdx !== -1) {
                                                                            nuovo[originalIdx] = { ...nuovo[originalIdx], gestito: nuovoStato };
                                                                        }
                                                                        return nuovo;
                                                                    });
                                                                } catch (err) {
                                                                    console.error("Errore aggiornamento gestito:", err);
                                                                }
                                                            }}
                                                        />
                                                    </td>
                                                </tr>
                                                {expandedRows.has(idx) && (
                                                    <tr className="expanded-row-compact">
                                                        <td colSpan="7">
                                                            <div className="expanded-content-compact">
                                                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '20px', marginBottom: '16px' }}>
                                                                    {/* Transazione 1 */}
                                                                    <div style={{ background: '#F8FAFC', padding: '12px', borderRadius: '8px', border: '1px solid #E2E8F0' }}>
                                                                        <h4 style={{ fontSize: '13px', fontWeight: '700', color: '#2E6DB1', margin: '0 0 10px 0', textTransform: 'uppercase' }}>
                                                                            Transazione 1
                                                                        </h4>
                                                                        <div className="expanded-grid-compact">
                                                                            <div className="expanded-item-compact">
                                                                                <span className="expanded-label-compact">Data</span>
                                                                                <span className="expanded-value-compact">{new Date(row.data_prima).toLocaleString()}</span>
                                                                            </div>
                                                                            <div className="expanded-item-compact">
                                                                                <span className="expanded-label-compact">Carburante</span>
                                                                                <span className="expanded-value-compact">{row.prodotto_primo}</span>
                                                                            </div>
                                                                            <div className="expanded-item-compact">
                                                                                <span className="expanded-label-compact">Importo</span>
                                                                                <span className="expanded-value-compact" style={{ color: '#10B981', fontWeight: '700' }}>{row.valore_primo?.toFixed(2)} €</span>
                                                                            </div>
                                                                            <div className="expanded-item-compact">
                                                                                <span className="expanded-label-compact">Driver</span>
                                                                                <span className="expanded-value-compact">{row.driver_primo || "-"}</span>
                                                                            </div>
                                                                            <div className="expanded-item-compact">
                                                                                <span className="expanded-label-compact">Referente</span>
                                                                                <span className="expanded-value-compact">{row.referente_primo || "-"}</span>
                                                                            </div>
                                                                        </div>
                                                                    </div>

                                                                    {/* Transazione 2 */}
                                                                    <div style={{ background: '#F8FAFC', padding: '12px', borderRadius: '8px', border: '1px solid #E2E8F0' }}>
                                                                        <h4 style={{ fontSize: '13px', fontWeight: '700', color: '#2E6DB1', margin: '0 0 10px 0', textTransform: 'uppercase' }}>
                                                                            Transazione 2
                                                                        </h4>
                                                                        <div className="expanded-grid-compact">
                                                                            <div className="expanded-item-compact">
                                                                                <span className="expanded-label-compact">Data</span>
                                                                                <span className="expanded-value-compact">{new Date(row.data_seconda).toLocaleString()}</span>
                                                                            </div>
                                                                            <div className="expanded-item-compact">
                                                                                <span className="expanded-label-compact">Carburante</span>
                                                                                <span className="expanded-value-compact">{row.prodotto_secondo}</span>
                                                                            </div>
                                                                            <div className="expanded-item-compact">
                                                                                <span className="expanded-label-compact">Importo</span>
                                                                                <span className="expanded-value-compact" style={{ color: '#10B981', fontWeight: '700' }}>{row.valore_secondo?.toFixed(2)} €</span>
                                                                            </div>
                                                                            <div className="expanded-item-compact">
                                                                                <span className="expanded-label-compact">Driver</span>
                                                                                <span className="expanded-value-compact">{row.driver_secondo || "-"}</span>
                                                                            </div>
                                                                            <div className="expanded-item-compact">
                                                                                <span className="expanded-label-compact">Referente</span>
                                                                                <span className="expanded-value-compact">{row.referente_secondo || "-"}</span>
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                </div>

                                                                {/* Dettaglio e Note */}
                                                                <div className="dettaglio-note-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '16px' }}>
                                                                    <div className="expanded-item-compact">
                                                                        <span className="expanded-label-compact">Dettaglio Controllo</span>
                                                                        <span className="expanded-value-compact">{row.controllo}</span>
                                                                    </div>
                                                                    <div className="expanded-item-compact">
                                                                        <span className="expanded-label-compact">Note</span>
                                                                        <div className="nota-wrapper" style={{ display: 'flex', gap: '10px', width: '100%', alignItems: 'flex-start' }}>
                                                                            <textarea className="nota-textarea"
                                                                                defaultValue={row.note || ""}
                                                                                id={`nota-ravv-${row.targa_dell_automezzo}-${row.data_prima}`}
                                                                                style={{
                                                                                    flex: 1,
                                                                                    minHeight: '60px',
                                                                                    padding: '8px',
                                                                                    border: '1px solid #E2E8F0',
                                                                                    borderRadius: '6px',
                                                                                    fontSize: '13px',
                                                                                    fontFamily: 'inherit',
                                                                                    resize: 'vertical',
                                                                                    boxSizing: 'border-box'
                                                                                }}
                                                                                placeholder="Aggiungi nota..."
                                                                            />
                                                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', alignItems: 'flex-start' }}>
                                                                                <button
                                                                                    onClick={async () => {
                                                                                        const textarea = document.getElementById(
                                                                                            `nota-ravv-${row.targa_dell_automezzo}-${row.data_prima}`
                                                                                        );
                                                                                        const nuovaNota = textarea.value;
                                                                                        if (nuovaNota === row.note) return;

                                                                                        try {
                                                                                            await axios.post(`${API_URL}/dkv/gestito-ravvicinate`, {
                                                                                                targa: row["targa_dell_automezzo"],
                                                                                                data_1: row["data_prima"],
                                                                                                data_2: row["data_seconda"],
                                                                                                gestito: row.gestito,
                                                                                                note: nuovaNota
                                                                                            });

                                                                                            setDati(prev => {
                                                                                                const nuovo = [...prev];
                                                                                                const originalIdx = dati.findIndex(d => d.targa_dell_automezzo === row.targa_dell_automezzo && d.data_prima === row.data_prima);
                                                                                                if (originalIdx !== -1) {
                                                                                                    nuovo[originalIdx] = { ...nuovo[originalIdx], note: nuovaNota };
                                                                                                }
                                                                                                return nuovo;
                                                                                            });
                                                                                            const key = `${row.targa_dell_automezzo}-${row.data_prima}`;
                                                                                            setNoteFeedback({ key, type: 'success' });
                                                                                            setTimeout(() => setNoteFeedback(prev => prev?.key === key ? null : prev), 3000);
                                                                                        } catch (err) {
                                                                                            console.error("Errore aggiornamento nota:", err);
                                                                                            const key = `${row.targa_dell_automezzo}-${row.data_prima}`;
                                                                                            setNoteFeedback({ key, type: 'error' });
                                                                                            setTimeout(() => setNoteFeedback(prev => prev?.key === key ? null : prev), 3000);
                                                                                        }
                                                                                    }}
                                                                                    style={{
                                                                                        padding: '6px 16px',
                                                                                        backgroundColor: '#3B82F6',
                                                                                        color: 'white',
                                                                                        border: 'none',
                                                                                        borderRadius: '6px',
                                                                                        fontSize: '13px',
                                                                                        cursor: 'pointer',
                                                                                        fontWeight: '500'
                                                                                    }}
                                                                                >
                                                                                    Salva Nota
                                                                                </button>
                                                                                {noteFeedback?.key === `${row.targa_dell_automezzo}-${row.data_prima}` && (
                                                                                    <span style={{
                                                                                        fontSize: '13px',
                                                                                        fontWeight: '600',
                                                                                        padding: '4px 12px',
                                                                                        borderRadius: '6px',
                                                                                        background: noteFeedback.type === 'success' ? '#22C55E' : '#EF4444',
                                                                                        color: '#FFFFFF'
                                                                                    }}>
                                                                                        {noteFeedback.type === 'success' ? '✓ Nota salvata!' : '✗ Errore nel salvataggio!'}
                                                                                    </span>
                                                                                )}
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                )}
                                            </React.Fragment>
                                        ))}
                                    </tbody>
                                </table>
                                </div>

                                {/* Paginazione dentro table-container per fullscreen */}
                                <div className="pagination-compact">
                                    <button
                                        className="pagination-btn-compact"
                                        disabled={page === 1}
                                        onClick={() => setPage(page - 1)}
                                    >
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <path d="M15 18l-6-6 6-6" />
                                        </svg>
                                        Prec
                                    </button>
                                    <div className="pagination-info-compact">
                                        <span className="pagination-text-compact">
                                            Pag <strong>{page}</strong> di <strong>{totalPages || 1}</strong>
                                        </span>
                                        <span className="pagination-total-compact">({total} risultati totali)</span>
                                    </div>
                                    <button
                                        className="pagination-btn-compact"
                                        disabled={page === totalPages || totalPages === 0}
                                        onClick={() => setPage(page + 1)}
                                    >
                                        Succ
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <path d="M9 18l6-6-6-6" />
                                        </svg>
                                    </button>
                                    <div className="items-per-page-compact">
                                        <span className="items-label-compact">Elementi per pagina:</span>
                                        <div className="items-buttons-compact">
                                            {[10, 20, 50, 100].map((num) => (
                                                <button
                                                    key={num}
                                                    className={`items-btn-compact ${limit === num ? "active" : ""}`}
                                                    onClick={() => {
                                                        setLimit(num);
                                                        setPage(1);
                                                    }}
                                                >
                                                    {num}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            </div>
                            </>
                        )}

                        {viewMode === 'cards' && (
                            <div className="cards-grid-compact">
                                {filteredDati.map((row, idx) => (
                                    <div className="data-card-compact" key={idx}>
                                        <div className="card-header-compact">
                                            <div className="card-badge-compact">{row.targa_dell_automezzo}</div>
                                            <div className="card-price-compact">{(row.valore_primo + row.valore_secondo).toFixed(2)} €</div>
                                        </div>
                                        <div className="card-body-compact">
                                            <div className="card-row-compact">
                                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                    <path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z"></path>
                                                    <circle cx="12" cy="10" r="3"></circle>
                                                </svg>
                                                <strong>Alimentazione:</strong> {row.alimentazione}
                                            </div>
                                            <div className="card-divider-compact"></div>

                                            <div style={{ marginBottom: '12px' }}>
                                                <p style={{ fontSize: '11px', fontWeight: '600', color: '#64748B', margin: '0 0 6px 0', textTransform: 'uppercase' }}>Transazione 1</p>
                                                <div style={{ fontSize: '12px', color: '#475569', lineHeight: '1.6' }}>
                                                    <div><strong>Data:</strong> {new Date(row.data_prima).toLocaleString()}</div>
                                                    <div><strong>Carburante:</strong> {row.prodotto_primo}</div>
                                                    <div><strong>Importo:</strong> {row.valore_primo?.toFixed(2)} €</div>
                                                    <div><strong>Driver:</strong> {row.driver_primo || "-"}</div>
                                                    <div><strong>Referente:</strong> {row.referente_primo || "-"}</div>
                                                </div>
                                            </div>

                                            <div style={{ marginBottom: '12px' }}>
                                                <p style={{ fontSize: '11px', fontWeight: '600', color: '#64748B', margin: '0 0 6px 0', textTransform: 'uppercase' }}>Transazione 2</p>
                                                <div style={{ fontSize: '12px', color: '#475569', lineHeight: '1.6' }}>
                                                    <div><strong>Data:</strong> {new Date(row.data_seconda).toLocaleString()}</div>
                                                    <div><strong>Carburante:</strong> {row.prodotto_secondo}</div>
                                                    <div><strong>Importo:</strong> {row.valore_secondo?.toFixed(2)} €</div>
                                                    <div><strong>Driver:</strong> {row.driver_secondo || "-"}</div>
                                                    <div><strong>Referente:</strong> {row.referente_secondo || "-"}</div>
                                                </div>
                                            </div>

                                            <div className="card-divider-compact"></div>

                                            <div className="card-row-compact">
                                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                    <circle cx="12" cy="12" r="10"></circle>
                                                    <polyline points="12 6 12 12 16 14"></polyline>
                                                </svg>
                                                <strong>Delta:</strong> {row.min_trascorsi
                                                    ? `${row.min_trascorsi.hours || 0}h ${row.min_trascorsi.minutes || 0}m`
                                                    : "-"}
                                            </div>

                                            <div className="card-row-compact">
                                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                                                    <polyline points="14 2 14 8 20 8"></polyline>
                                                    <line x1="12" y1="18" x2="12" y2="12"></line>
                                                    <line x1="9" y1="15" x2="15" y2="15"></line>
                                                </svg>
                                                <strong>Dettaglio:</strong> {row.controllo}
                                            </div>
                                        </div>

                                        <div className="card-footer-compact">
                                            <div style={{ marginBottom: '10px' }}>
                                                <p style={{fontSize: '11px', fontWeight: '600', color: '#64748B', margin: '0 0 6px 0', textTransform: 'uppercase', letterSpacing: '0.5px'}}>
                                                    Note
                                                </p>
                                                <div className="nota-wrapper" style={{ display: 'flex', gap: '10px', width: '100%', alignItems: 'flex-start' }}>
                                                    <textarea className="nota-textarea"
                                                        defaultValue={row.note || ""}
                                                        id={`nota-ravv-card-${row.targa_dell_automezzo}-${row.data_prima}`}
                                                        style={{
                                                            flex: 1,
                                                            minHeight: '60px',
                                                            padding: '8px',
                                                            border: '1px solid #E2E8F0',
                                                            borderRadius: '6px',
                                                            fontSize: '13px',
                                                            fontFamily: 'inherit',
                                                            resize: 'vertical',
                                                            boxSizing: 'border-box'
                                                        }}
                                                        placeholder="Aggiungi nota..."
                                                    />
                                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', alignItems: 'flex-start' }}>
                                                        <button
                                                            onClick={async () => {
                                                                const textarea = document.getElementById(
                                                                    `nota-ravv-card-${row.targa_dell_automezzo}-${row.data_prima}`
                                                                );
                                                                const nuovaNota = textarea.value;
                                                                if (nuovaNota === row.note) return;

                                                                try {
                                                                    await axios.post(`${API_URL}/dkv/gestito-ravvicinate`, {
                                                                        targa: row["targa_dell_automezzo"],
                                                                        data_1: row["data_prima"],
                                                                        data_2: row["data_seconda"],
                                                                        gestito: row.gestito,
                                                                        note: nuovaNota
                                                                    });

                                                                    setDati(prev => {
                                                                        const nuovo = [...prev];
                                                                        const originalIdx = dati.findIndex(d => d.targa_dell_automezzo === row.targa_dell_automezzo && d.data_prima === row.data_prima);
                                                                        if (originalIdx !== -1) {
                                                                            nuovo[originalIdx] = { ...nuovo[originalIdx], note: nuovaNota };
                                                                        }
                                                                        return nuovo;
                                                                    });
                                                                    const key = `card-${row.targa_dell_automezzo}-${row.data_prima}`;
                                                                    setNoteFeedback({ key, type: 'success' });
                                                                    setTimeout(() => setNoteFeedback(prev => prev?.key === key ? null : prev), 3000);
                                                                } catch (err) {
                                                                    console.error("Errore aggiornamento nota:", err);
                                                                    const key = `card-${row.targa_dell_automezzo}-${row.data_prima}`;
                                                                    setNoteFeedback({ key, type: 'error' });
                                                                    setTimeout(() => setNoteFeedback(prev => prev?.key === key ? null : prev), 3000);
                                                                }
                                                            }}
                                                            style={{
                                                                padding: '6px 16px',
                                                                backgroundColor: '#3B82F6',
                                                                color: 'white',
                                                                border: 'none',
                                                                borderRadius: '6px',
                                                                fontSize: '13px',
                                                                cursor: 'pointer',
                                                                fontWeight: '500'
                                                            }}
                                                        >
                                                            Salva Nota
                                                        </button>
                                                        {noteFeedback?.key === `card-${row.targa_dell_automezzo}-${row.data_prima}` && (
                                                            <span style={{
                                                                fontSize: '13px',
                                                                fontWeight: '600',
                                                                padding: '4px 12px',
                                                                borderRadius: '6px',
                                                                background: noteFeedback.type === 'success' ? '#22C55E' : '#EF4444',
                                                                color: '#FFFFFF'
                                                            }}>
                                                                {noteFeedback.type === 'success' ? '✓ Nota salvata!' : '✗ Errore nel salvataggio!'}
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="card-divider-compact"></div>

                                            <label className="checkbox-label-compact">
                                                <input
                                                    type="checkbox"
                                                    className="checkbox-compact"
                                                    checked={row.gestito}
                                                    onChange={async () => {
                                                        const nuovoStato = !row.gestito;
                                                        try {
                                                            await axios.post(`${API_URL}/dkv/gestito-ravvicinate`, {
                                                                targa: row["targa_dell_automezzo"],
                                                                data_1: row["data_prima"],
                                                                data_2: row["data_seconda"],
                                                                gestito: nuovoStato,
                                                                note: row.note
                                                            });

                                                            setDati(prev => {
                                                                const nuovo = [...prev];
                                                                const originalIdx = dati.findIndex(d => d.targa_dell_automezzo === row.targa_dell_automezzo && d.data_prima === row.data_prima);
                                                                if (originalIdx !== -1) {
                                                                    nuovo[originalIdx] = { ...nuovo[originalIdx], gestito: nuovoStato };
                                                                }
                                                                return nuovo;
                                                            });
                                                        } catch (err) {
                                                            console.error("Errore aggiornamento gestito:", err);
                                                        }
                                                    }}
                                                />
                                                Gestito
                                            </label>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}

                    </>
                )}
            </div>

        </div>
    );
};

export default TransazioniRavvicinate;
